import { isArray, isUndefined, noop } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import ReactSelect from 'react-select'
import ReactCreatableSelect from 'react-select/creatable'
import { EMPTY_FORM_VALUE } from 'utils-next/forms'
import { ExtendableStyles, Testable } from 'utils-next/types'

import { FormHelperText } from '@mui/material'

import { useHelperText } from '../Common/Input.hooks'
import { customStyles, defaultSelectComponents, useCustomSelectTheme } from './Select.partials'
import { CommonSelectProps, CreatableProps, CustomSelectProps, InternalSelectValue } from './Select.types'

// Interface allows us to replace params
export type SelectProps = ExtendableStyles &
  Testable &
  CommonSelectProps &
  CreatableProps &
  CustomSelectProps & {
    selectProps?: CommonSelectProps & CreatableProps & CustomSelectProps
    classes?: {
      select?: string
      helperText?: string
    }
  }

function isSameReference(a: any, b: any) {
  return a === b
}

function defaultLabel(a: any) {
  return a.toString()
}

function defaultValue(a: any) {
  return !isUndefined(a.value) ? a.value : a
}

export function Select(
  {
    className,
    label,
    placeholder,
    options = [],
    error,
    required,
    helperText: helperTextProp,
    components = {},
    menuPosition = 'absolute' as SelectProps['menuPosition'],
    disabled,
    style,
    isMulti,
    'data-testid': dataTestId,
    isClearable = true,
    allowCreation,
    selectProps = {} as any,
    classes: classesProp = {},
    getOptionSelected = isSameReference,
    getOptionLabel = defaultLabel,
    getOptionValue = defaultValue,
    onChange = noop,
    onCreateOption = noop,
    value,
    ...otherProps
  }: SelectProps,
  ref: React.Ref<HTMLSelectElement>
) {
  const theme = useCustomSelectTheme()
  const helperText = useHelperText({ error, helperText: helperTextProp })

  // Injected props outside of "react-select" dependencies
  const extraProps = {
    label,
    error,
    required,
  }

  const selectComponents = useMemo(
    () => ({
      ...defaultSelectComponents,
      ...components,
    }),
    [components]
  )

  const data = useMemo(
    () =>
      options.map((option: any) => ({
        value: getOptionValue(option),
        label: getOptionLabel(option),
      })),
    [getOptionLabel, options]
  )

  const selectedValue = useMemo(() => {
    if (isUndefined(value) || (isMulti && !isArray(value))) {
      return !isMulti ? EMPTY_FORM_VALUE : []
    }
    if (!isMulti) {
      const indexOfData = data.findIndex((option) => getOptionSelected(getOptionValue(option), value))
      const hasValue = indexOfData !== -1
      return !hasValue
        ? {
            value,
            label: getOptionLabel(value),
          }
        : data[indexOfData] || EMPTY_FORM_VALUE
    } else {
      return value.map((option: any) => ({ value: option, label: getOptionLabel(value) }))
    }
  }, [getOptionLabel, getOptionSelected, data, value])

  const handleChange = useCallback(
    (option: InternalSelectValue | InternalSelectValue[] | null, action: any) => {
      if (!isMulti && !isArray(option)) {
        onChange(option ? getOptionValue(option) : EMPTY_FORM_VALUE, action)
      } else if (isArray(option)) {
        onChange((option || []).map(getOptionValue), action)
      } else {
        onChange(isMulti ? [] : EMPTY_FORM_VALUE, action)
      }
    },
    [onChange]
  )

  const handleCreation = useCallback(
    (string: string) => {
      const newOption = onCreateOption(string)
      onChange(newOption, { action: 'select-option', option: newOption })
    },
    [onChange, onCreateOption]
  )

  const SelectComponent = (allowCreation ? ReactCreatableSelect : ReactSelect) as any
  return (
    <div className={className} data-testid={dataTestId} style={style}>
      <SelectComponent
        className={classesProp.select}
        isClearable={isClearable}
        styles={customStyles}
        options={data}
        components={selectComponents}
        menuPosition={menuPosition}
        placeholder={placeholder || ''}
        theme={theme}
        isDisabled={disabled}
        innerRef={ref}
        isMulti={isMulti}
        value={selectedValue}
        onChange={handleChange}
        onCreateOption={handleCreation}
        getOptionValue={getOptionValue}
        {...otherProps}
        {...extraProps}
        {...selectProps}
      />
      {helperText.isVisible && (
        <FormHelperText className={classesProp.helperText} error={Boolean(error)}>
          {helperText.children}
        </FormHelperText>
      )}
    </div>
  )
}
